import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ScrollText, Clock, IndianRupee, Layers, Send, CheckCircle2 } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

const STATUS_COLOR = {
  draft: "border-bm-dim/40 text-bm-dim",
  sent: "border-bm-cyan/40 text-bm-cyan",
  responses_in: "border-bm-amber/40 text-bm-amber",
  shortlist: "border-purple-400/40 text-purple-300",
  awarded: "border-bm-green/40 text-bm-green",
  closed: "border-white/15 text-bm-muted",
};

function StatusPill({ status }) {
  return (
    <span className={`label-mono px-1.5 py-0.5 border ${STATUS_COLOR[status] || "border-white/15 text-bm-muted"}`}>
      {status.replaceAll("_", " ").toUpperCase()}
    </span>
  );
}

function RFPCard({ r, idx, focused, cardRef }) {
  return (
    <div
      ref={focused ? cardRef : null}
      data-testid={`rfp-card-${r.id}`}
      className={`bm-card fade-up p-5 flex flex-col gap-3 transition-all duration-700 ${
        focused ? "ring-2 ring-bm-amber shadow-[0_0_24px_rgba(242,201,76,0.35)]" : ""
      }`}
      style={{ animationDelay: `${Math.min(idx * 50, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-mono text-bm-cyan">{r.category}</span>
          {r.subcategory && <span className="label-mono text-bm-amber">· {r.subcategory}</span>}
        </div>
        <div data-testid={`rfp-status-${r.id}`}><StatusPill status={r.status} /></div>
      </div>

      <h3 className="font-heading text-lg font-black leading-tight text-white" data-testid={`rfp-title-${r.id}`}>
        {r.title}
      </h3>

      <p className="text-sm text-bm-text/90 leading-relaxed">{r.summary}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-white/5 pt-3">
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><IndianRupee className="w-3 h-3" />TARGET</div>
          <div className="font-mono text-sm text-white">{r.target_price_inr || "—"}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><Layers className="w-3 h-3" />MOQ</div>
          <div className="font-mono text-sm text-white">{r.target_moq ? `${(r.target_moq/1000).toFixed(0)}k` : "—"}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><Send className="w-3 h-3" />SENT TO</div>
          <div className="font-mono text-sm text-white">{(r.sent_to_supplier_ids || []).length}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />RESPONSES</div>
          <div className={`font-mono text-sm ${r.responses_received > 0 ? "text-bm-green" : "text-bm-muted"}`}>{r.responses_received || 0}</div>
        </div>
      </div>

      {r.sent_to_suppliers?.length > 0 && (
        <div className="border-t border-white/5 pt-3">
          <div className="label-mono text-bm-dim mb-1.5">SENT TO SUPPLIERS</div>
          <div className="space-y-1.5">
            {r.sent_to_suppliers.filter(Boolean).map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-xs font-mono">
                <span className="text-white">{s.name}</span>
                <span className="text-bm-dim">·</span>
                <span className="text-bm-muted">{s.kind?.replaceAll("_", " ")}</span>
                <span className="text-bm-dim">·</span>
                <span className={s.rating >= 90 ? "text-bm-green" : "text-bm-amber"}>{s.rating}</span>
                {r.awarded_supplier_id === s.id && (
                  <span className="ml-auto label-mono text-bm-green border border-bm-green/30 px-1 py-0.5">AWARDED</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {r.notes && (
        <p className="border-t border-white/5 pt-3 text-xs text-bm-muted leading-relaxed italic">{r.notes}</p>
      )}

      <div className="flex items-center justify-between text-[10px] font-mono pt-2 border-t border-white/5">
        <span className="text-bm-dim">RESPONSE BY · {r.target_response_date || "—"}</span>
        <div className="flex items-center gap-1 text-bm-dim">
          <Clock className="w-3 h-3" />{formatGeneratedAt(r.generated_at || r.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function RFPs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const focusRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.rfps(statusFilter ? { status: statusFilter } : {}).then((d) => {
      setItems(d.items || []);
      setLoading(false);
    });
  }, [statusFilter]);

  // Scroll the focused card into view + auto-clear the param after 3s so the
  // highlight only flashes once.
  useEffect(() => {
    if (!focusId || loading) return;
    const t = setTimeout(() => {
      focusRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    const clearT = setTimeout(() => {
      setSearchParams({}, { replace: true });
    }, 3500);
    return () => { clearTimeout(t); clearTimeout(clearT); };
  }, [focusId, loading, items.length, setSearchParams]);

  const statuses = ["draft", "sent", "responses_in", "shortlist", "awarded", "closed"];

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber flex items-center gap-2">
            <ScrollText className="w-3.5 h-3.5" />REQUEST-FOR-PROPOSAL PIPELINE
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            Out to the market.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Active RFPs sent to suppliers, with response status, shortlists, and awards. One step downstream
            of Briefs.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="rfp-status-filters">
          <button
            onClick={() => setStatusFilter("")}
            data-testid="rfp-filter-all"
            className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
              !statusFilter ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
            }`}
          >
            ALL
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`rfp-filter-${s}`}
              className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
                statusFilter === s ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
              }`}
            >
              {s.replaceAll("_", " ").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING RFPS...</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No RFPs in this status.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rfp-grid">
        {items.map((r, i) => (
          <RFPCard key={r.id} r={r} idx={i}
            focused={focusId === r.id}
            cardRef={focusRef}
          />
        ))}
      </div>

      {/* If the focused RFP isn't in the current filter, surface a banner with a one-click reset. */}
      {focusId && !loading && !items.find((r) => r.id === focusId) && (
        <div className="bm-card-elev p-3 flex items-center justify-between" data-testid="focus-not-found">
          <span className="text-xs font-mono text-bm-amber">
            Focused RFP not in current filter.
          </span>
          <button
            onClick={() => setStatusFilter("")}
            data-testid="clear-filter-for-focus"
            className="px-2.5 py-1 text-xs font-mono bg-bm-amber text-black hover:bg-white"
          >
            CLEAR FILTER
          </button>
        </div>
      )}
    </div>
  );
}
